import { Form } from "@client/content/controls";
import { SINGLE_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import CartItems from "@client/content/modules/Cart/Items/component";
import {
  OrderBillingDetails,
  OrderItemLine,
  OrderShippingDetails
} from "@client/content/modules/OrderConfirmation/Content/component";
import { isDummyOrder } from "@client/content/modules/OrderConfirmation/lib";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type FormFieldInfo } from "@client/controls/Form/component";
import DateTimePicker from "@client/controls/Inputs/DateTimePicker/component";
import NumberInput from "@client/controls/Inputs/NumberInput/component";
import Select, { DynamicSelect } from "@client/controls/Inputs/Select/component";
import Modal from "@client/controls/Modal/container";
import date from "@client/lib/date";
import { joinDefined } from "@client/lib/misc";
import { Status, type WithIsEditMode } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import * as React from "react";
import api from "../api";
import { type MemberRecurringOrders } from "../types";

export type MapProps = RootContainerProps &
  WithIsEditMode & {
    apiInfo: ModuleApiInfo;
    detail: MemberRecurringOrders.ItemDetail | null;

    isVisible: boolean;
    noImageUrl: string;
    status: string;
  };

export type ActionProps = {
  onDownloadClick: () => void;
  onClose: () => void;
  onUpdate: (data: MemberRecurringOrders.View.UpdateDetail) => void;
};

type UpdateOrderProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  detail: MemberRecurringOrders.ItemDetail;
};
const UpdateOrder = (props: UpdateOrderProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const fields = React.useMemo(
    (): FormFieldInfo[] => [
      {
        Component: DynamicSelect,
        isDisabled: true,
        label: i18n.status,
        name: "subscriptionStatusKey",
        defaultValue: props.detail.subscription.subscriptionStatusKey,
        componentOpts: {
          options: options.getGetOptions("shop", "subscriptionStatuses", {
            cultureCode: props.apiInfo.baseParams.cultureCode
          })
        }
      },
      {
        Component: Select,
        isDisabled: true,
        label: i18n.product,
        name: "productVariantId",
        defaultValue: props.detail.subscription.productVariantId,
        colSpan: 8,
        componentOpts: {
          options: [{ value: props.detail.subscription.productVariantId, label: props.detail.subscription.productName }]
        }
      },
      {
        Component: NumberInput,
        isDisabled: true,
        label: i18n.quantity,
        name: "quantity",
        defaultValue: props.detail.subscription.quantity,
        colSpan: 4,
        componentOpts: { min: 1, fractionalDigits: 0 }
      },
      {
        Component: NumberInput,
        isDisabled: true,
        label: i18n.maxRepeats,
        name: "repeatLimit",
        defaultValue: props.detail.subscription.repeatLimit,
        componentOpts: { min: 1, fractionalDigits: 0 }
      },
      {
        Component: DateTimePicker,
        isDisabled: true,
        label: i18n.nextRepeatDate,
        name: "nextRepeatDate",
        defaultValue: props.detail.subscription.nextRepeatDate,
        componentOpts: { hasTime: false }
      }
    ],
    [props.apiInfo, props.detail]
  );

  return (
    <div className="update_order">
      <h4>{i18n.updateOrder}</h4>
      <Form {...getRootContainerProps(props)} fields={fields} />
    </div>
  );
};

type ContentProps = RootContainerProps &
  WithIsEditMode & {
    apiInfo: ModuleApiInfo;
    detail: MemberRecurringOrders.ItemDetail;

    isVisible: boolean;
    noImageUrl: string;
  };
const Content = (props: ContentProps) => {
  const i18n = useI18n();

  const [isDownloading, setIsDownloading] = React.useState(false);

  const download = React.useCallback(async () => {
    if (isDummyOrder(props.detail.order.orderId)) return;

    setIsDownloading(true);
    try {
      await api.downloadInvoice(props.apiInfo, props.detail.subscription.subscriptionId);
    } finally {
      setIsDownloading(false);
    }
  }, [props.apiInfo, props.detail.order.orderId, props.detail.subscription.subscriptionId]);

  return (
    <div className="content wsc_order_summary">
      <div className="top">
        <div className="summary">
          <h4>{i18n.recurringOrderSummary}</h4>

          <OrderItemLine name={i18n.order}>{props.detail.order.number}</OrderItemLine>

          <OrderItemLine name={i18n.placedDate}>{date.formatDateTime(props.detail.order.orderedOnDate)}</OrderItemLine>

          <OrderItemLine name={i18n.customer}>
            {joinDefined(" ", props.detail.order.billingFirstName, props.detail.order.billingLastName)}
          </OrderItemLine>

          <OrderItemLine name={i18n.paymentStatus}>{props.detail.order.orderPaymentStatusName}</OrderItemLine>
          <OrderItemLine name={i18n.orderStatus}>{props.detail.order.minOrderStatusName}</OrderItemLine>

          <OrderItemLine name={i18n.shippingStatus}>{props.detail.order.minOrderShippingStatusName}</OrderItemLine>
        </div>

        <OrderShippingDetails order={props.detail.order} orderItems={props.detail.orderItems} />
      </div>

      <div className="wsc_cart_items">
        <CartItems
          redirectOnClick={!props.isEditMode}
          imageSize={48}
          currencyCode={props.detail.order.currencyCode}
          items={props.detail.orderItems}
          noImageUrl={props.noImageUrl}
          shouldDisplayTax={false}
        />
      </div>

      <div className="bottom summary">
        <UpdateOrder {...getRootContainerProps(props)} apiInfo={props.apiInfo!} detail={props.detail} />

        <OrderBillingDetails billingReferences={props.detail.billingReferences} order={props.detail.order} />
      </div>

      <div className="buttons" style={SINGLE_BUTTONS_STYLE}>
        <ModuleButton {...getRootContainerProps(props)} onClick={download} isDisabled={isDownloading}>
          {i18n.download}
        </ModuleButton>
      </div>
    </div>
  );
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  return (
    <Modal
      className="wsc_member_order_detail"
      hasBackdrop
      isAdminColor={false}
      isDraggable={false}
      isCentered
      isVisible={props.isVisible}
      isLoading={props.status === Status.Loading}
      onClose={props.onClose}
      width={800}
    >
      <Modal.Header title={i18n.orderDetails} />
      <Modal.Body>{props.detail?.order && <Content {...props} detail={props.detail} />}</Modal.Body>
    </Modal>
  );
};
